import React, { useEffect } from "react";
import { connect, useSelector, useDispatch } from "react-redux";
import { List } from "antd";
import dayjs from "dayjs";
import { ClipboardClock } from "lucide-react";
import PopupButton from "components/button/PopupButton";
import OvertimeForm from "views/console/overtimes/components/OvertimeForm";
import { MODULE_OVERTIME } from "variables/constants";
import { getTimeCardList } from "../../../../../redux/timecard/timeCardState";
import { getOvertimeList } from "../../../../../redux/overtime/overtimeState";

const RecentAttendanceList = ({ doSearch, pagination }) => {
  const recordList = useSelector(
    (state) => state.timeCard.timeCardList?.items || []
  );
  const dispatch = useDispatch();
  const overtimeItems = useSelector(
    (state) => state.overtime.overtimeList?.items ?? []
  );

  useEffect(() => {
    dispatch(getOvertimeList({ paginate: 100 }));
  }, [dispatch]);

  useEffect(() => {
    const pageSize = pagination?.pageSize || 5;
    doSearch({ paginate: pageSize });
  }, [doSearch, pagination?.pageSize]);

  return (
    <div>
      <h4 className="text-sm font-semibold mb-3 dark:text-white">Recent Attendance Records</h4>

      <List
        dataSource={recordList}
        pagination={{
          pageSize: pagination?.pageSize,
          total: pagination?.total || 0,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
          onChange: (page, pageSize) => doSearch({ paginate: pageSize, page }),
        }}
        renderItem={(record) => {
          const hasSchedule = record?.is_on_shift === true;
          const isRestDay = record?.is_rest_day === true;
          const isHoliday = record?.is_holiday === true;
          const isAbsent =
            hasSchedule &&
            !isRestDay &&
            !isHoliday &&
            !record?.tmc_punch_in;

          const overtimeRecord = overtimeItems.find(
            (ot) => ot.ots_timecard_id === record.tmc_id
          );

          let overtimeButtonLabel = "File Overtime";

            if (overtimeRecord) {
              switch (overtimeRecord.ots_status_id) {
                case 66:
                  overtimeButtonLabel = null; 
                  break;
                case 67:
                  overtimeButtonLabel = null; 
                  break;
                case 68:
                case 69:
                  overtimeButtonLabel = "Resubmit Overtime";
                  break;
              }
            }

          let overtimeStatusLabel = null;

            if (overtimeRecord) {
              switch (overtimeRecord.ots_status_id) {
                case 66:
                  overtimeStatusLabel = (
                    <span className="status-indicator overtime text-[10px]">
                      Overtime Filed
                    </span>
                  );
                  break;
                case 67:
                  overtimeStatusLabel = (
                    <span className="status-indicator approved text-[10px]">
                      Overtime Approved
                    </span>
                  );
                  break;
              }
            }

          return (
            <List.Item
              key={record?.tmc_id}
              className="!p-0 !border-0 mt-2"
            >
              <div className="px-3 w-full border rounded-xl p-4 dark:border-slate-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 dark:text-white">
                      <span className="text-sm font-medium dark:text-white ">
                        {dayjs(record?.tmc_date).format("dddd")}
                      </span>
                      <span>•</span>
                      <p className="font-semibold text-sm">
                        {dayjs(record?.tmc_date).format("MMM D, YYYY")}
                      </p>

                      {hasSchedule && (
                        <span className="flex gap-1">
                          {isAbsent ? (
                            <span className="status-indicator absent text-[10px]">Absent</span>
                          ) : (
                            <>
                              {record?.late_duration && (
                                <span className="status-indicator late text-[10px]">
                                  Late ({record?.late_duration})
                                </span>
                              )}
                              {record?.undertime_duration && (
                                <span className="status-indicator undertime text-[10px]">
                                  Undertime ({record?.undertime_duration})
                                </span>
                              )}
                              {!record?.late_duration && !record?.undertime_duration && (
                                <span className="status-indicator on-time text-[10px]">On Time</span>
                              )}
                            </>
                          )}
                        </span>
                      )}


                      {isRestDay && (
                        <span className="status-indicator rest-day text-[10px]">Rest Day</span>
                      )}
                      {isHoliday && (
                        <span className="status-indicator holiday text-[10px]">Holiday</span>
                      )}
                    </div>

                    {hasSchedule && (
                      <div className="flex flex-wrap gap-1 text-xs">
                        <p className="dark:text-slate-100">Schedule:</p>
                        <span className="text-xs font-medium dark:text-white">{record?.shift_string}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    {hasSchedule && (
                      <div>
                        <span className="text-sm font-medium dark:text-slate-100">Punches</span>
                        <div className="flex flex-wrap gap-3 text-xs dark:text-white">
                          <span>
                            In:{" "}
                            <span className="font-medium">
                              {record.tmc_punch_in
                                ? dayjs(record.tmc_punch_in).format("hh:mm A")
                                : "—"}
                            </span>
                          </span>
                          <span>•</span>
                          <span>
                            Out:{" "}
                            <span className="font-medium">
                              {record.tmc_punch_out
                                ? dayjs(record.tmc_punch_out).format("hh:mm A")
                                : "—"}
                            </span>
                          </span>
                          <span>•</span>
                          <span>
                            Lunch: <span className="font-medium">60 mins</span>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 flex justify-end">
                    {hasSchedule && record?.tmc_worked_hours >= 9.0 && (
                      overtimeStatusLabel ? (
                        overtimeStatusLabel
                      ) : overtimeButtonLabel ? (
                        <PopupButton
                          id={`file_overtime_${record.tmc_id}`}
                          label={overtimeButtonLabel}
                          title={overtimeButtonLabel}
                          icon={ClipboardClock}
                          btnClass="flex items-center justify-center rounded-xl text-sm font-medium h-6 p-2 text-xs bg-blue-500 hover:!bg-blue-600 text-white"
                          moduleId={MODULE_OVERTIME}
                          action="add"
                          useCustomClass={true}
                        >
                          {({ closeModal }) => (
                            <div className="max-h-[80vh] overflow-y-auto">
                              <OvertimeForm
                                timeCard={record}
                                recordId={overtimeRecord?.ots_id ?? 0}
                                mode="edit"
                                onCancel={closeModal}
                              />
                            </div>
                          )}
                        </PopupButton>
                      ) : null
                    )}

                  </div>

                  <div className="text-right">
                    <p className="text-xs dark:text-white">Total Hours</p>
                    <p className="text-lg font-bold text-emerald-600 dark:text-emerald-500">
                      {record?.tmc_worked_hours > 0
                        ? Number(record?.tmc_worked_hours).toFixed(2) + " hrs"
                        : "-"}
                    </p>
                  </div>

                </div>
              </div>
            </List.Item>
          );
        }}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  pagination: state.timeCard.timeCardList?.pagination,
});

const mapDispatchToProps = (dispatch) => ({
  doSearch: (filters) => dispatch(getTimeCardList(filters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RecentAttendanceList);
